import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getTournamentBracketGraph } from '../services/tournamentsV2';
import {
  TournamentBracketGraph,
  TournamentV2Match,
  BracketType,
} from '../types';
import './TournamentBracketTree.css';

interface TournamentBracketTreeProps {
  tournamentId: number;
  autoRefresh?: boolean; // Auto-refresh every 5 seconds for live updates
  refreshInterval?: number; // Custom refresh interval in ms (default: 5000)
}

const TournamentBracketTree: React.FC<TournamentBracketTreeProps> = ({
  tournamentId,
  autoRefresh = false,
  refreshInterval = 5000,
}) => {
  const { t } = useTranslation();
  const [bracketData, setBracketData] = useState<TournamentBracketGraph | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedBracket, setSelectedBracket] = useState<BracketType>('WB');

  useEffect(() => {
    loadBracket();
  }, [tournamentId]);

  // Auto-refresh for live updates
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      loadBracket(true); // Silent refresh (no loading state)
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, tournamentId]);

  const loadBracket = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setError(null);
      const data = await getTournamentBracketGraph(tournamentId);
      setBracketData(data);
    } catch (err) {
      console.error('Error loading bracket:', err);
      setError(err instanceof Error ? err.message : 'Failed to load bracket');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bracket-tree-loading">
        <div className="loading-spinner"></div>
        <p>{t('tournaments.bracket.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bracket-tree-error">
        <p className="error-message">{error}</p>
        <button onClick={() => loadBracket()} className="btn btn-primary">
          {t('common.retry')}
        </button>
      </div>
    );
  }

  if (!bracketData || bracketData.matches.length === 0) {
    return (
      <div className="bracket-tree-empty">
        <p>{t('tournaments.bracket.noBracket')}</p>
      </div>
    );
  }

  // Group matches by bracket and round
  const matchesByBracket = bracketData.matches.reduce((acc, match) => {
    if (!acc[match.bracket]) acc[match.bracket] = {};
    if (!acc[match.bracket][match.round_number]) acc[match.bracket][match.round_number] = [];
    acc[match.bracket][match.round_number].push(match);
    return acc;
  }, {} as Record<BracketType, Record<number, TournamentV2Match[]>>);

  const availableBrackets = Object.keys(matchesByBracket) as BracketType[];

  // Get current bracket matches
  const currentBracketMatches = matchesByBracket[selectedBracket] || {};
  const roundNumbers = Object.keys(currentBracketMatches).map(Number).sort((a, b) => a - b);
  const maxRound = Math.max(...roundNumbers, 0);

  return (
    <div className="tournament-bracket-tree">
      {/* Bracket Tabs (for double elimination) */}
      {availableBrackets.length > 1 && (
        <div className="bracket-tabs">
          {availableBrackets.map(bracket => (
            <button
              key={bracket}
              className={`bracket-tab ${selectedBracket === bracket ? 'active' : ''}`}
              onClick={() => setSelectedBracket(bracket)}
            >
              {t(`tournaments.bracket.${bracket}`)}
            </button>
          ))}
        </div>
      )}

      {/* Horizontal Bracket Tree */}
      <div className="bracket-tree-container">
        <div className="bracket-tree-scroll">
          <div className="bracket-tree-rounds">
            {roundNumbers.map((roundNum) => {
              const matches = (currentBracketMatches[roundNum] || []).sort((a, b) => a.match_number - b.match_number);
              const isLastRound = roundNum === maxRound;
              
              return (
                <div key={roundNum} className="bracket-round">
                  {/* Round Header */}
                  <div className="round-header">
                    <h4>
                      {isLastRound 
                        ? t('tournaments.bracket.final')
                        : `${t('tournaments.bracket.round')} ${roundNum}`
                      }
                    </h4>
                  </div>

                  {/* Matches */}
                  <div className="round-matches">
                    {matches.map((match, matchIndex) => {
                      const matchAssignments = bracketData.assignments.filter(
                        a => a.match_id === match.id
                      );
                      const matchResult = bracketData.results.find(
                        r => r.match_id === match.id
                      );

                      const player1 = matchAssignments.find(a => a.slot_number === 1);
                      const player2 = matchAssignments.find(a => a.slot_number === 2);

                      const isWinner = (slotNumber: 1 | 2) => {
                        return matchResult && matchResult.winner_slot === slotNumber;
                      };

                      const getMatchStateClass = () => {
                        if (match.state === 'completed') return 'completed';
                        if (match.state === 'in_progress') return 'in-progress';
                        if (match.state === 'ready') return 'ready';
                        if (match.state === 'void') return 'walkover';
                        return 'pending';
                      };

                      const isWalkover = match.state === 'void';
                      
                      // Determine if this match is part of a pair (for connection lines)
                      const isPairTop = matchIndex % 2 === 0 && matchIndex < matches.length - 1;
                      const isPairBottom = matchIndex % 2 === 1;
                      const hasPair = isPairTop || isPairBottom;

                      return (
                        <div
                          key={match.id}
                          className={`bracket-match ${getMatchStateClass()} ${hasPair ? 'has-pair' : ''} ${isPairTop ? 'pair-top' : ''} ${isPairBottom ? 'pair-bottom' : ''}`}
                        >
                          {/* Match Number Badge */}
                          <div className="match-badge">
                            <span className="match-number">M{match.match_number}</span>
                          </div>

                          {/* Players */}
                          <div className="match-players">
                            <div className={`player ${isWinner(1) ? 'winner' : ''} ${!player1 ? 'tbd' : ''}`}>
                              <span className="player-name">
                                {player1 ? player1.player_name : <span className="tbd-text">TBD</span>}
                              </span>
                              <span className="player-score">
                                {matchResult ? matchResult.slot_1_score : ''}
                              </span>
                            </div>

                            <div className={`player ${isWinner(2) ? 'winner' : ''} ${!player2 ? 'tbd' : ''}`}>
                              <span className="player-name">
                                {player2 ? player2.player_name : <span className="tbd-text">TBD</span>}
                              </span>
                              <span className="player-score">
                                {matchResult ? matchResult.slot_2_score : ''}
                              </span>
                            </div>
                          </div>

                          {/* Walkover Badge */}
                          {isWalkover && (
                            <div className="walkover-badge">
                              🏆 {t('tournaments.matches.walkover')}
                            </div>
                          )}

                          {/* State Badge */}
                          {!isWalkover && match.state !== 'pending' && (
                            <div className={`state-badge state-${match.state}`}>
                              {t(`tournaments.matches.state.${match.state}`)}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Connection Lines (connector to next round) */}
                  {!isLastRound && (
                    <div className="round-connector">
                      {matches.map((_, idx) => (
                        <div key={idx} className="connector-line"></div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Auto-refresh indicator */}
        {autoRefresh && (
          <div className="auto-refresh-indicator">
            <span className="pulse-dot"></span>
            {t('tournaments.bracket.liveUpdate')}
          </div>
        )}
      </div>
    </div>
  );
};

export default TournamentBracketTree;

